import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Camera, Loader2, Trash2, Pencil, CheckCircle2, XCircle, X, ShieldAlert, MapPin, Navigation, LocateFixed } from "lucide-react";
import { useField } from "../FieldContext";
import { Mono, Btn, assetReadiness, photoSrc, Field, TextArea } from "../ui";
import { getPosition, distanceKm, bearingTo, compass8 } from "../lib/geo";

const PHASES = [
  { code: "pre_disaster", name: "Pre-disaster" },
  { code: "damage", name: "Damage" },
  { code: "repair", name: "Repair" },
  { code: "signage", name: "Signage" },
];

export default function AssetCapture() {
  const { assetId } = useParams();
  const { store, version, bump, sync } = useField();
  const [asset, setAsset] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [phase, setPhase] = useState("damage");
  const [pending, setPending] = useState(null); // { file, pos, chainage, bearing, note }
  const [locating, setLocating] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  const nav = useNavigate();

  useEffect(() => {
    (async () => {
      const a = await store.getRec("assets", assetId);
      setAsset(a);
      setPhotos(await store.getPhotos(assetId));
    })();
  }, [store, assetId, version]);

  const blobUrls = useMemo(() => {
    const map = {};
    photos.forEach((p) => p.hasBlob && p.blob && (map[p.id] = URL.createObjectURL(p.blob)));
    return map;
  }, [photos]);
  useEffect(() => () => Object.values(blobUrls).forEach((u) => URL.revokeObjectURL(u)), [blobUrls]);
  const srcFor = (p) => blobUrls[p.id] || photoSrc(p);

  // Reference photo navigator: guide the crew back to the earlier capture spot.
  const refPhoto = useMemo(() => {
    if (phase === "pre_disaster") return null;
    const withGps = (ph) => photos.filter((p) => p.phase === ph && p.latitude != null);
    return withGps("pre_disaster")[0] || (phase !== "damage" ? withGps("damage")[0] : null) || null;
  }, [photos, phase]);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setLocating(true);
    let pos = {};
    try {
      pos = await getPosition();
    } catch (err) {
      /* allow capture without GPS but warn later */
    }
    setLocating(false);
    setPending({ file, pos, chainage: asset?.chainage_start || "", bearing: pos.bearing ?? "", note: "" });
  };

  const savePhoto = async () => {
    setSaving(true);
    try {
      await store.queuePhoto(assetId, asset.claim_id, phase, pending.file, {
        latitude: pending.pos.latitude ?? null,
        longitude: pending.pos.longitude ?? null,
        gps_accuracy: pending.pos.gps_accuracy ?? null,
        bearing: pending.bearing === "" ? null : Number(pending.bearing),
        chainage: pending.chainage,
        note: pending.note,
      });
      setPending(null);
      bump();
      sync();
    } finally {
      setSaving(false);
    }
  };

  const removePhoto = async (id) => {
    if (!window.confirm("Delete this photo?")) return;
    await store.deletePhoto(id);
    bump();
    sync();
  };

  if (!asset) return <div className="text-white/50 text-sm" data-testid="field-asset-loading">Loading asset…</div>;

  const r = assetReadiness(asset, photos);
  const shown = photos.filter((p) => p.phase === phase);

  return (
    <div className="space-y-5" data-testid="field-asset-capture">
      <div className="flex items-start justify-between gap-3">
        <div>
          <Mono className="text-gold-400 text-[0.6rem]">Evidence capture</Mono>
          <h1 className="font-heading text-xl mt-1 leading-snug">{asset.name}</h1>
          <p className="text-white/45 text-xs mt-0.5">{asset.asset_type} · {asset.funding_measure}</p>
        </div>
        <button onClick={() => nav(`/field/asset/${assetId}/edit`)} data-testid="field-edit-asset" className="text-gold-300 border border-gold-500/25 rounded-sm p-2">
          <Pencil className="h-4 w-4" />
        </button>
      </div>

      {/* readiness */}
      <div className="border border-gold-500/25 bg-navy-900/50 rounded-sm p-4" data-testid="field-readiness">
        <div className="flex items-center justify-between mb-2">
          <Mono className="text-white/50 text-[0.55rem]">Audit readiness</Mono>
          <span className={`text-sm font-semibold ${r.pct === 100 ? "text-emerald-400" : r.pct >= 50 ? "text-gold-400" : "text-red-400"}`}>{r.pct}%</span>
        </div>
        <div className="space-y-1">
          {r.checks.map(([label, ok]) => (
            <div key={label} className="flex items-center gap-2 text-xs">
              {ok ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" /> : <XCircle className="h-3.5 w-3.5 text-white/30" />}
              <span className={ok ? "text-white/70" : "text-white/40"}>{label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* phase tabs */}
      <div className="grid grid-cols-4 gap-1.5" data-testid="field-phase-tabs">
        {PHASES.map((p) => (
          <button
            key={p.code}
            onClick={() => setPhase(p.code)}
            data-testid={`field-phase-${p.code}`}
            className={`py-2 rounded-sm text-xs border transition-colors ${phase === p.code ? "bg-gold-500 text-navy-950 font-semibold border-gold-500" : "border-gold-500/25 text-white/60"}`}
          >
            {p.name}
            <span className="block text-[0.55rem] opacity-70">{r.counts[p.code] || 0}</span>
          </button>
        ))}
      </div>

      {refPhoto && <RefNavigator refPhoto={refPhoto} src={srcFor(refPhoto)} />}

      {/* photos grid */}
      {shown.length === 0 ? (
        <div className="border border-dashed border-gold-500/25 rounded-sm p-8 text-center text-white/40 text-xs" data-testid="field-phase-empty">
          No {PHASES.find((p) => p.code === phase).name.toLowerCase()} photos yet.
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-2" data-testid="field-photo-grid">
          {shown.map((p) => (
            <div key={p.id} className="relative aspect-square border border-gold-500/20 rounded-sm overflow-hidden bg-navy-900" data-testid={`field-photo-${p.id}`}>
              <img src={srcFor(p)} alt="" className="w-full h-full object-cover" />
              {p.synced === false && <span className="absolute top-1 left-1 bg-amber-400 text-navy-950 text-[0.5rem] font-bold px-1 rounded-sm">↑</span>}
              {p.latitude == null && <span className="absolute bottom-1 left-1"><MapPin className="h-3 w-3 text-red-400" /></span>}
              <button onClick={() => removePhoto(p.id)} data-testid={`field-photo-del-${p.id}`} className="absolute top-1 right-1 bg-navy-950/80 rounded-sm p-1">
                <Trash2 className="h-3 w-3 text-red-300" />
              </button>
            </div>
          ))}
        </div>
      )}

      <input ref={fileRef} type="file" accept="image/*" capture="environment" onChange={onPick} className="hidden" data-testid="field-photo-input" />
      <Btn onClick={() => fileRef.current?.click()} disabled={locating} testid="field-capture-btn">
        {locating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />} Capture {PHASES.find((p) => p.code === phase).name} photo
      </Btn>

      <button onClick={() => nav("/field/swms")} data-testid="field-swms-link" className="w-full flex items-center justify-center gap-2 text-gold-300/80 text-xs border border-gold-500/20 rounded-sm py-2.5">
        <ShieldAlert className="h-3.5 w-3.5" /> Review &amp; acknowledge SWMS before starting work
      </button>

      {/* capture-detail modal */}
      {pending && (
        <div className="fixed inset-0 bg-navy-950/90 z-40 flex items-end sm:items-center justify-center p-4" data-testid="field-capture-modal">
          <div className="bg-navy-900 border border-gold-500/30 rounded-sm w-full max-w-md p-5 space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-lg">Photo details</h2>
              <button onClick={() => setPending(null)}><X className="h-5 w-5 text-white/60" /></button>
            </div>
            <img src={URL.createObjectURL(pending.file)} alt="preview" className="w-full max-h-56 object-cover rounded-sm border border-gold-500/20" />
            <div className={`text-xs flex items-center gap-1.5 ${pending.pos.latitude != null ? "text-emerald-300" : "text-red-300"}`} data-testid="field-capture-gps">
              <MapPin className="h-3.5 w-3.5" />
              {pending.pos.latitude != null ? `${pending.pos.latitude}, ${pending.pos.longitude} (±${pending.pos.gps_accuracy ?? "?"}m)` : "No GPS — required for audit. Enable location."}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Chainage" value={pending.chainage} onChange={(v) => setPending((s) => ({ ...s, chainage: v }))} testid="field-capture-chainage" />
              <Field label="Bearing °" value={pending.bearing} onChange={(v) => setPending((s) => ({ ...s, bearing: v }))} testid="field-capture-bearing" />
            </div>
            <TextArea label="Note" value={pending.note} onChange={(v) => setPending((s) => ({ ...s, note: v }))} testid="field-capture-note" rows={2} />
            <Btn onClick={savePhoto} disabled={saving} testid="field-capture-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Save photo
            </Btn>
          </div>
        </div>
      )}
    </div>
  );
}

function RefNavigator({ refPhoto, src }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");

  const locate = async () => {
    setBusy(true);
    setErr("");
    try {
      const pos = await getPosition();
      const here = [pos.latitude, pos.longitude];
      const there = [refPhoto.latitude, refPhoto.longitude];
      const m = Math.round(distanceKm(here, there) * 1000);
      const dir = bearingTo(here, there);
      setResult({ m, dir: Math.round(dir), compass: compass8(dir), acc: pos.gps_accuracy });
    } catch {
      setErr("Could not get your position — enable location services.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-sky-400/30 bg-sky-400/5 rounded-sm p-3" data-testid="field-ref-nav">
      <div className="flex items-center gap-3">
        <img src={src} alt="reference" className="h-14 w-14 object-cover rounded-sm border border-sky-400/30 shrink-0" />
        <div className="flex-1 min-w-0">
          <Mono className="text-sky-300 text-[0.5rem] flex items-center gap-1"><Navigation className="h-3 w-3" /> Reference photo navigator</Mono>
          <p className="text-white/55 text-[0.65rem] mt-1 font-mono truncate">
            {Number(refPhoto.latitude).toFixed(5)}, {Number(refPhoto.longitude).toFixed(5)}
            {refPhoto.bearing != null && ` · shot facing ${Math.round(refPhoto.bearing)}°`}
          </p>
        </div>
        <button onClick={locate} disabled={busy} data-testid="field-ref-locate"
          className="shrink-0 border border-sky-400/40 text-sky-300 rounded-sm px-3 py-2 text-xs flex items-center gap-1.5 disabled:opacity-50">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <LocateFixed className="h-3.5 w-3.5" />} Guide me
        </button>
      </div>
      {result && (
        <p className="text-sky-200 text-xs mt-2.5" data-testid="field-ref-result">
          {result.m <= Math.max(8, result.acc || 0)
            ? "You're at the reference spot — match the framing of the thumbnail."
            : `Move ~${result.m} m ${result.compass} (bearing ${result.dir}°) to reach the reference spot`}
          {refPhoto.bearing != null && result.m > Math.max(8, result.acc || 0) ? `, then face ${Math.round(refPhoto.bearing)}°.` : ""}
        </p>
      )}
      {err && <p className="text-red-300 text-xs mt-2.5" data-testid="field-ref-error">{err}</p>}
    </div>
  );
}
